import React from 'react';
import Card from '../ui/Card';
import Badge from '../ui/Badge';

const buildPoints = (values, width, height, padding) => {
  const span = values.length - 1;
  return values.map((value, idx) => {
    const x = padding + (span === 0 ? 0 : (idx / span) * (width - padding * 2));
    const y = height - padding - (Math.max(0, Math.min(100, value)) / 100) * (height - padding * 2);
    return { x, y, value };
  });
};

export default function AttendanceTrendChart({
  points = [],
  threshold,
  meta = [],
  isLoading = false,
  error = ''
}) {
  if (isLoading) {
    return <Card className="p-5">Loading trend…</Card>;
  }
  if (error) {
    return <Card className="p-5 sp-alert sp-alert--critical text-sm">{error}</Card>;
  }
  if (!points.length) {
    return <Card className="p-5 text-sm sp-muted">No attendance trend data.</Card>;
  }

  const width = 260;
  const height = 120;
  const padding = 12;
  const chartPoints = buildPoints(points, width, height, padding);
  const polyline = chartPoints.map((p) => `${p.x},${p.y}`).join(' ');
  const thresholdY = height - padding - (Math.max(0, Math.min(100, threshold)) / 100) * (height - padding * 2);

  return (
    <Card className="p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="sp-value" style={{ fontWeight: 600 }}>Trend (8 weeks)</h3>
          <p className="text-xs sp-muted">Attendance trend with safeguarding threshold</p>
        </div>
        <Badge variant="review">Threshold {threshold}%</Badge>
      </div>
      <div role="img" aria-label="Attendance trend chart with threshold line">
        <svg width="100%" height="140" viewBox={`0 0 ${width} ${height}`} data-testid="trend-chart">
          <line
            x1={padding}
            x2={width - padding}
            y1={thresholdY}
            y2={thresholdY}
            stroke="#ef4444"
            strokeDasharray="4 4"
            strokeWidth="1.5"
            data-testid="threshold-line"
          />
          <polyline
            points={polyline}
            fill="none"
            stroke="#0f172a"
            strokeWidth="2"
          />
          {chartPoints.map((point, idx) => {
            const isBelow = point.value < threshold;
            const metaEntry = meta[idx];
            const tooltip = metaEntry
              ? `${metaEntry.label}: ${point.value}% (${metaEntry.sessions} sessions, ${metaEntry.absences} absences)`
              : `Week ${idx + 1}: ${point.value}%`;
            return (
              <circle
                key={`${idx}-${point.value}`}
                cx={point.x}
                cy={point.y}
                r={4}
                fill={isBelow ? '#ef4444' : '#2563eb'}
              >
                <title>{tooltip}</title>
              </circle>
            );
          })}
        </svg>
      </div>
    </Card>
  );
}
